import { useEffect, useState } from 'react';
import { fetchAnalytics } from '../../lib/cms-api';

interface AnalyticsData {
  days: number;
  pageviews: number;
  conversions: number;
  visitors: number;
  conversionRate: number;
  topPages: { key: string; count: number }[];
  conversionsByName: { key: string; count: number }[];
  conversionsByPath: { key: string; count: number }[];
  campaigns: { campaign: string; pageviews: number; conversions: number }[];
  byDay: { day: string; pv: number; conv: number }[];
}

const CONV_LABEL: Record<string, string> = {
  whatsapp_click: 'WhatsApp 點擊',
  tel_click: '電話點擊',
  contact_nav: '前往聯絡頁',
  cta: '其他 CTA',
};

export default function AnalyticsView() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [err, setErr] = useState('');
  const [days, setDays] = useState(14);

  const load = async (d: number) => {
    setErr('');
    try {
      const r = await fetchAnalytics(d);
      setData(r);
    } catch (e) {
      setErr('無法載入數據。');
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err) return <div className="bg-white rounded-lg shadow-sm border p-5 text-gray-500">{err}</div>;
  if (!data) return <div className="bg-white rounded-lg shadow-sm border p-5 text-gray-400">載入中…</div>;

  const maxPv = Math.max(1, ...data.byDay.map((d) => d.pv));

  const Card = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
    <div className="bg-white rounded-lg shadow-sm border p-5">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-bold text-navy-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  const Bar = ({ label, count, max, gold }: { label: string; count: number; max: number; gold?: boolean }) => (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-xs text-gray-600 w-1/2 truncate" title={label}>
        {label}
      </span>
      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${gold ? 'bg-gold-500' : 'bg-navy-500'}`}
          style={{ width: `${Math.round((count / max) * 100)}%` }}
        />
      </div>
      <span className="text-xs font-semibold text-gray-700 w-10 text-right">{count}</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-navy-900">進站分析（第一方數據 · 零 cookie）</h2>
        <select
          className="border border-gray-200 rounded px-3 py-2 text-sm"
          value={days}
          onChange={(e) => {
            const d = Number(e.target.value);
            setDays(d);
            load(d);
          }}
        >
          <option value={7}>最近 7 天</option>
          <option value={14}>最近 14 天</option>
          <option value={30}>最近 30 天</option>
          <option value={90}>最近 90 天</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card label="瀏覽量 Pageviews" value={data.pageviews} />
        <Card label="訪客（估計）" value={data.visitors} />
        <Card label="WhatsApp／電話點擊" value={data.conversions} />
        <Card label="轉化率" value={`${data.conversionRate}%`} sub="點擊 ÷ 瀏覽" />
      </div>

      {/* Daily trend */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <p className="text-sm font-semibold text-gray-800 mb-3">每日瀏覽量</p>
        <div className="flex items-end gap-1 h-28">
          {data.byDay.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end group relative">
              <div
                className="w-full bg-navy-500 rounded-t group-hover:bg-gold-500 transition-colors"
                style={{ height: `${Math.max(4, (d.pv / maxPv) * 100)}%` }}
                title={`${d.day}：${d.pv} 瀏覽 / ${d.conv} 點擊`}
              ></div>
              <span className="text-[9px] text-gray-400 mt-1">{d.day.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Conversions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <p className="text-sm font-semibold text-gray-800 mb-2">轉化類型</p>
          {data.conversionsByName.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">尚無點擊數據。</p>
          ) : (
            data.conversionsByName.map((c) => (
              <Bar key={c.key} label={CONV_LABEL[c.key] || c.key} count={c.count} max={data.conversions} gold />
            ))
          )}
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <p className="text-sm font-semibold text-gray-800 mb-2">哪些頁面帶來 WhatsApp／電話點擊（商機訊號）</p>
          {data.conversionsByPath.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">尚無數據——點擊累積後，這裡會顯示「哪一頁最能帶客」。</p>
          ) : (
            data.conversionsByPath.map((c) => (
              <Bar key={c.key} label={c.key} count={c.count} max={data.conversionsByPath[0].count} gold />
            ))
          )}
        </div>
      </div>

      {/* Top pages */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <p className="text-sm font-semibold text-gray-800 mb-2">熱門頁面 Top {data.topPages.length}</p>
        {data.topPages.map((p) => (
          <Bar key={p.key} label={p.key} count={p.count} max={data.topPages[0].count} />
        ))}
      </div>

      {data.campaigns.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <p className="text-sm font-semibold text-gray-800 mb-2">UTM Campaign</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400">
                <th className="pb-2">Campaign</th>
                <th className="pb-2 text-right">瀏覽</th>
                <th className="pb-2 text-right">點擊</th>
              </tr>
            </thead>
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.campaign} className="border-t border-gray-100">
                  <td className="py-2">{c.campaign}</td>
                  <td className="py-2 text-right">{c.pageviews}</td>
                  <td className="py-2 text-right">{c.conversions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 px-1">
        數據來源：網站訪客瀏覽器直接上報（無 cookie、擋廣告插件也擋不掉）。訪客數以瀏覽器 ID 估算。
        AI 爬蟲抓取屬伺服器層數據，請以 Cloudflare AI Crawl Control / 日誌為準。
      </p>
    </div>
  );
}
